import type { HoursOfOperationDetail } from "./configuration.js";
import { connectToWeekly } from "./hours-connect.js";
import { evaluateHours, localWallTime } from "./hours-now.js";
import type { QueueSnapshotMetrics } from "./rewind.js";
import type { RewindCause, RewindCauseKind, RewindConfiguration, RewindContact, RewindMinute, RewindSourceVerdict } from "./rewind-records.js";

/**
 * Whether a Connect hours of operation, as the configuration history kept
 * it, was open at an instant: an override whose dates hold the local day
 * replaces the week (an override with no windows is closed all day),
 * otherwise the weekly windows decide - read with the same `evaluateHours`
 * the lander uses.
 */
export function hoursOpenAt(detail: HoursOfOperationDetail, at: Date): boolean {
  const day = localWallTime(detail.timezone, at).stamp.slice(0, 10);
  const override = detail.overrides.find((o) => o.from <= day && day <= o.till);
  const entries = override ? override.config : detail.config;
  return evaluateHours({ timezone: detail.timezone, weekly: connectToWeekly(entries), holidays: [] }, at).open;
}

/**
 * ADR-0016 section 5: why a contact waited, from the sources that saw it,
 * as a listed set of causes - and "we cannot say" (`undetermined`) when the
 * sources disagree or saw too little. Pure: the rewind route calls it over
 * what it read, the agents route again with what the agent events add.
 * design.md "Rewind" is the table this implements.
 */

/** Under this, the record says the wait was short, whatever it felt like. */
export const MISREMEMBERED_SECONDS = 120;
/** Fewer observed minutes than this share of the wait, and the snapshot cannot carry a verdict. */
const MIN_COVERAGE = 0.5;

export interface CauseInput {
  /** The minutes of the wait (or the asked range), in order. */
  minutes: RewindMinute[];
  /** Per minute of `minutes`, whether some other queue on the instance had an agent available. */
  elsewhereAvailable?: boolean[];
  configuration: RewindConfiguration;
  contact?: RewindContact;
  /** From the agents route only: who the agent events show missing or rejecting the contact. */
  missedBy?: Array<{ name: string; state: string }>;
}

const seconds = (s: number) => (s >= 60 ? `${Math.floor(s / 60)} m ${s % 60} s` : `${s} s`);
const most = (n: number, of: number) => of > 0 && n * 2 > of;

export function evaluateCause(input: CauseInput): RewindCause {
  const { minutes, configuration, contact } = input;
  const observed = minutes.filter((m): m is RewindMinute & { snapshot: QueueSnapshotMetrics } => m.snapshot !== undefined);
  const snaps = observed.map((m) => m.snapshot);
  const count = (pred: (s: QueueSnapshotMetrics) => boolean) => snaps.filter(pred).length;
  const availableMinutes = count((s) => s.agents_available > 0);
  const onlineMinutes = count((s) => s.agents_online > 0);
  const derivedWaiting = minutes.filter((m) => m.derived > 0).length;
  const snapshotWaiting = count((s) => s.contacts_in_queue > 0);
  const coverage = `${observed.length} of ${minutes.length} minutes observed`;

  const verdict = (cause: RewindCauseKind, evidence: string[], overrides: Partial<Record<RewindSourceVerdict["source"], Omit<RewindSourceVerdict, "source">>> = {}): RewindCause => ({
    cause,
    evidence,
    sources: [
      overrides.snapshot ? { source: "snapshot", ...overrides.snapshot } : observed.length ? { source: "snapshot", status: "agrees", detail: coverage } : { source: "snapshot", status: "missing", detail: "No snapshot for any minute of the wait." },
      overrides.contacts ? { source: "contacts", ...overrides.contacts } : derivedWaiting || contact ? { source: "contacts", status: "agrees", detail: contact ? `The record says ${contact.waitSeconds !== undefined ? seconds(contact.waitSeconds) : "an open wait"} in the queue.` : `The records show contacts waiting in ${derivedWaiting} minutes.` } : { source: "contacts", status: "missing", detail: "No contact record shows anyone in the queue." },
      overrides.configuration ? { source: "configuration", ...overrides.configuration } : configuration.available ? { source: "configuration", status: "agrees", detail: `${configuration.profiles.length} routing profile(s) fed the queue.` } : { source: "configuration", status: "missing", detail: "No configuration version on file for the minute." },
      overrides.agent_events ? { source: "agent_events", ...overrides.agent_events } : input.missedBy ? { source: "agent_events", status: "agrees", detail: "Read for the minute; no agent missed the contact." } : { source: "agent_events", status: "missing", detail: "Not read - open the agents at a minute." },
    ],
  });

  // Misremembered: the record is the measure of the wait.
  if (contact?.waitSeconds !== undefined && contact.waitSeconds < MISREMEMBERED_SECONDS) {
    return verdict("misremembered", [`The contact record says it waited ${seconds(contact.waitSeconds)} in the queue.`]);
  }

  // Closed: the queue's hours said closed when she arrived - unless agents were in fact available on it.
  if (configuration.hours && !configuration.hours.openAtEnqueue) {
    if (availableMinutes === 0) {
      return verdict("closed", [`${configuration.hours.name} (${configuration.hours.timezone}) was closed at ${configuration.at}.`], {
        configuration: { status: "agrees", detail: `Hours of operation as of ${configuration.hours.observedAt}.` },
      });
    }
  }

  if (!observed.length || observed.length < minutes.length * MIN_COVERAGE) {
    return verdict("undetermined", [`Only ${coverage}: too few to say what the queue looked like.`], {
      snapshot: { status: "missing", detail: coverage },
    });
  }

  // The two queue-side sources must agree that someone was waiting.
  if (snapshotWaiting === 0 && most(derivedWaiting, minutes.length)) {
    return verdict("undetermined", [`The snapshot saw nobody waiting in ${observed.length} minutes; the contact records show someone waiting in ${derivedWaiting}.`], {
      snapshot: { status: "disagrees", detail: "Nobody waiting in any observed minute." },
      contacts: { status: "disagrees", detail: `Someone waiting in ${derivedWaiting} minutes.` },
    });
  }

  const profilesFeed = configuration.profiles.length > 0;
  const elsewhere = (input.elsewhereAvailable ?? []).filter(Boolean).length;

  // Nobody could take the queue.
  if (onlineMinutes === 0) {
    if (configuration.available && !profilesFeed) {
      return verdict("misrouted", ["No routing profile included this queue, so no agent could be offered the contact."], {
        configuration: { status: "agrees", detail: "No profile fed the queue at the minute." },
      });
    }
    if (elsewhere > 0) {
      return verdict("misrouted", [`No agent online for this queue in any of ${observed.length} observed minutes, while agents were available on other queues in ${elsewhere}.`, "The agents on the instance were not on a routing profile that takes this queue."]);
    }
    return verdict("unstaffed", [`No agent online for this queue in any of ${observed.length} observed minutes.`]);
  }

  if (input.missedBy && input.missedBy.length > 0) {
    return verdict("offered_and_missed", input.missedBy.map((a) => `Offered to ${a.name}, who ${a.state === "REJECTED" ? "rejected" : "missed"} it.`), {
      agent_events: { status: "agrees", detail: `${input.missedBy.length} agent(s) missed or rejected the contact.` },
    });
  }

  // A profile that takes the queue only after longer than she waited could not have offered it to anyone.
  const wait = contact?.waitSeconds;
  if (wait !== undefined && profilesFeed && configuration.profiles.every((p) => p.delaySeconds >= wait)) {
    return verdict("misrouted", configuration.profiles.map((p) => `${p.name} takes this queue only after ${seconds(p.delaySeconds)} (${p.channel}); the contact left after ${seconds(wait)}.`), {
      configuration: { status: "agrees", detail: "Every profile that fed the queue delayed it past the wait." },
    });
  }

  const busy = count((s) => s.agents_staffed > 0 && s.agents_available === 0 && s.agents_on_contact + s.agents_after_contact_work >= s.agents_staffed);
  const first = snaps[0]!;
  const last = snaps[snaps.length - 1]!;
  const growing = last.contacts_in_queue > first.contacts_in_queue || last.oldest_contact_age_seconds > first.oldest_contact_age_seconds;
  if (most(busy, observed.length) && !most(availableMinutes, observed.length) && growing) {
    return verdict("overloaded", [
      `Every staffed agent was on a contact or in after-contact work in ${busy} of ${observed.length} minutes, with none available.`,
      `The queue went from ${first.contacts_in_queue} to ${last.contacts_in_queue} waiting; the oldest wait from ${seconds(first.oldest_contact_age_seconds)} to ${seconds(last.oldest_contact_age_seconds)}.`,
    ]);
  }

  const away = count((s) => s.agents_online > 0 && s.agents_available === 0 && s.agents_non_productive > 0);
  if (most(away, observed.length) && !most(availableMinutes, observed.length)) {
    const peak = Math.max(...snaps.map((s) => s.agents_non_productive));
    return verdict("staffed_but_unavailable", [`Agents were online but none available in ${away} of ${observed.length} minutes; up to ${peak} in a non-productive status.`]);
  }

  return verdict("undetermined", [
    `An agent was available for this queue in ${availableMinutes} of ${observed.length} minutes, and no listed cause fits.`,
    profilesFeed ? `Profiles feeding it: ${configuration.profiles.map((p) => `${p.name} (priority ${p.priority}, delay ${seconds(p.delaySeconds)})`).join("; ")}.` : "No routing configuration on file for the minute.",
  ]);
}
